/* eslint-disable */
import { detectDocumentIssues } from '@api/form-verification';
import WarningModal from '@components/modals/warning';
import { FileUploadState } from '@components/ui/file-tile/states';
import VerificationContext, {
  VerificationContextType
} from '@containers/form-verification/verification/context';
import { fileToBase64 } from '@global/helpers/general';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import { Box, Button, IconButton, SxProps, Tooltip, Typography } from '@mui/material';
import { useContext, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { getFileUploadStateIcon } from './constants';
import WarningMessage, { DocumentDetectionReport } from './warning-message';
interface FileTileProps {
  file: any;
  disabled?: boolean;
  deleteFile?: () => void;
  url?: string;
  label?: string;
  sx?: SxProps;
  showVerifiedIcon?: boolean;
  shouldProcess?: boolean;
  setFilePickerOpen?: React.Dispatch<React.SetStateAction<boolean>>;
  allowedFileTypes?: string[] | undefined;
}
function FileTile(props: FileTileProps) {
  const {
    file,
    disabled,
    deleteFile,
    url,
    label,
    sx,
    showVerifiedIcon,
    shouldProcess = false,
    setFilePickerOpen,
    allowedFileTypes
  } = props;
  const context = (useContext(VerificationContext) as VerificationContextType) ?? {};
  const { states } = context;
  const setGlobalFileUploadState = states?.setGlobalFileUploadState;
  const [localFileUploadState, setLocalFileUploadState] = useState(FileUploadState.NULL);
  const [error, setError] = useState<string>('');
  const [showModal, setShowModal] = useState<boolean>(false);
  const [documentDetectionReport, setDocumentDetectionReport] =
    useState<DocumentDetectionReport | null>(null);

  useEffect(() => {
    if (!shouldProcess || !setGlobalFileUploadState) return;
    const processFile = async () => {
      setGlobalFileUploadState(FileUploadState.PROCESSING);
      setLocalFileUploadState(FileUploadState.PROCESSING);
      let base64File = null;
      try {
        base64File = await fileToBase64(file);
      } catch {
        setLocalFileUploadState(FileUploadState.NULL);
        setGlobalFileUploadState(FileUploadState.NULL); // or set to a specific error state if you have one
        return;
      }
      const res = await detectDocumentIssues({ file: base64File });
      setDocumentDetectionReport(res?.body);
      if (res?.body?.showWarning) {
        setGlobalFileUploadState(FileUploadState.WARNING);
        setLocalFileUploadState(FileUploadState.WARNING);
      } else if (res?.statusCode >= 400) {
        setGlobalFileUploadState(FileUploadState.ERROR);
        setLocalFileUploadState(FileUploadState.ERROR);
        setError(res?.data?.error);
      } else {
        setLocalFileUploadState(FileUploadState.COMPLETED);
        setGlobalFileUploadState(FileUploadState.COMPLETED);
      }
    };
    // processFile();
  }, [file, shouldProcess]);
  const fileName = (label ?? file?.name) || '';
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        backgroundColor: '#fff',
        borderRadius: '8px',
        border: '1px solid',
        borderColor: disabled ? 'font.light' : 'primary.main',
        padding: '1rem .8rem',
        cursor: 'pointer',
        ...sx
        //   opacity: isDisabled ? ".65" : "1"
      }}
      style={{ textDecoration: 'none' }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
          <Button
            disableRipple
            component={Link}
            to={url ?? (file instanceof File ? URL.createObjectURL(file) : '')}
            target="_blank"
            style={{
              all: 'unset',
              display: 'flex',
              alignItems: 'center',
              columnGap: '1rem',
              flex: 1,
              width: '85%'
            }}>
            <Box
              sx={{
                backgroundColor: disabled && !showVerifiedIcon ? '#eee' : '#E4F1FF',
                borderRadius: '70%',
                height: '2.6rem',
                width: '2.6rem',
                display: 'grid',
                placeItems: 'center',
                ml: '.465rem'
              }}>
              <AttachFileIcon
                sx={{
                  height: '1.8rem',
                  width: '1.8rem',
                  color: disabled && !showVerifiedIcon ? 'font.medium' : 'primary.main'
                }}
              />
            </Box>
            <Tooltip title={file?.name?.length > 10 ? file?.name : ''}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  flex: 1,
                  minWidth: 0
                }}>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: '400',
                    color: disabled ? 'font.main' : 'primary.main',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    flex: 1,
                    minWidth: 0,
                    maxWidth: '200px'
                  }}
                  noWrap>
                  {fileName}
                </Typography>
                {localFileUploadState !== undefined && getFileUploadStateIcon(localFileUploadState)}
              </Box>
            </Tooltip>
            {showVerifiedIcon && (
              <CheckCircleIcon sx={{ color: 'success.main', height: '1.9rem', width: '1.9rem' }} />
            )}
          </Button>
          {!disabled && (
            <IconButton
              sx={{ p: '.1rem' }}
              onClick={(e) => {
                e.stopPropagation();
                if (deleteFile) {
                  deleteFile();
                }
              }}>
              <CloseIcon sx={{ height: '2rem', width: '2rem' }} />
            </IconButton>
          )}
        </Box>
        {[FileUploadState.WARNING, FileUploadState.ERROR].includes(localFileUploadState) && (
          <>
            <Typography
              variant="body2"
              sx={{ color: 'grey', ml: 2, mt: 0.5, cursor: 'pointer' }}
              onClick={() => setShowModal(true)}>
              {localFileUploadState === FileUploadState.WARNING
                ? 'The uploaded file may be smudgy, blurry, or cropped. You may upload again.'
                : 'Something went wrong. Please click to see the error.'}
            </Typography>
            <WarningModal
              open={showModal}
              handleProceed={async () => {
                setShowModal(false);
                if (setFilePickerOpen) setFilePickerOpen(true);
                if (deleteFile) {
                  deleteFile();
                }
              }}
              art={<></>}
              closeText={localFileUploadState === FileUploadState.WARNING ? 'Proceed Anyway' : ''}
              proceedText="Upload Again"
              handleClose={() => setShowModal(false)}
              message={
                <WarningMessage
                  documentDetectionReport={documentDetectionReport}
                  fileUploadState={localFileUploadState}
                  error={error}
                  allowedFileTypes={allowedFileTypes}
                />
              }
            />
          </>
        )}
      </Box>
    </Box>
  );
}

export default FileTile;
